import React, { useEffect, useMemo, useState } from 'react';
import { cashApi } from '../../api/cash.Api';
import type { CashSummary, CreateMovementPayload, UpsertClosurePayload } from '../../types/cash';

import { CashFilters } from '../../components/Cash/CashFilters';
import { CashKpiCards } from '../../components/Cash/CashKpiCards';
import { CashDrawerMovementsTable } from '../../components/Cash/CashDrawerMovementsTable';
import { CashClosurePanel } from '../../components/Cash/CashClosurePanel';
import { CashDrawerMovementModal } from '../../components/Cash/CashDrawerMovementModal';

import { useAuth } from '../../auth/AuthContext';

function todayISODate(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function CashPage() {
  const { token } = useAuth();

  const [date, setDate] = useState<string>(todayISODate());
  const [posId, setPosId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<CashSummary | null>(null);
  const [error, setError] = useState<string>('');

  const [movementModalOpen, setMovementModalOpen] = useState(false);

  const posIdParam = useMemo(() => (posId.trim() ? posId.trim() : undefined), [posId]);

  async function load() {
    if (!token) {
      setSummary(null);
      setError('Token mancante: fai login.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await cashApi.getSummary(token, date, posIdParam);
      setSummary(data);
    } catch (e: any) {
      setError(e?.message ?? 'Errore caricamento');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, date, posIdParam]);

  async function onCreateMovement(payload: CreateMovementPayload) {
    if (!token) return;

    await cashApi.createMovement(token, payload);
    setMovementModalOpen(false);
    await load();
  }

  async function onSaveClosure(payload: UpsertClosurePayload) {
    if (!token) return;

    await cashApi.upsertClosure(token, payload);
    await load();
  }

  return (
    <div className="container-fluid py-3">
      <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
        <h1 className="h4 mb-0">Cassa</h1>

        <div className="d-flex gap-2">
          <button className="btn btn-primary" onClick={() => setMovementModalOpen(true)} disabled={!token}>
            + Movimento cassetto
          </button>
          <button className="btn btn-outline-secondary" onClick={load} disabled={loading || !token}>
            {loading ? 'Aggiorno…' : 'Aggiorna'}
          </button>
        </div>
      </div>

      <CashFilters date={date} posId={posId} onChangeDate={setDate} onChangePosId={setPosId} />

      {error ? (
        <div className="alert alert-danger mt-3 mb-0" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="alert alert-secondary mt-3 mb-0" role="alert">
          Caricamento…
        </div>
      ) : null}

      {summary ? (
        <>
          <div className="mt-3">
            <CashKpiCards summary={summary} />
          </div>

          <div className="row g-3 mt-1">
            <div className="col-12 col-xl-8">
              <div className="card">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <div className="fw-semibold">Movimenti cassetto</div>
                  <span className="badge text-bg-secondary">
                    Net:{' '}
                    {summary.drawerMovements.net.toLocaleString('it-IT', {
                      style: 'currency',
                      currency: 'EUR',
                    })}
                  </span>
                </div>
                <div className="card-body">
                  <CashDrawerMovementsTable items={summary.drawerMovements.items} />
                </div>
              </div>
            </div>

            <div className="col-12 col-xl-4">
              <div className="card">
                <div className="card-header fw-semibold">Chiusura cassa</div>
                <div className="card-body">
                  <CashClosurePanel
                    date={date}
                    posId={posIdParam}
                    closure={summary.closure}
                    onSave={onSaveClosure}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}

      <CashDrawerMovementModal
        open={movementModalOpen}
        onClose={() => setMovementModalOpen(false)}
        defaultDate={date}
        defaultPosId={posIdParam}
        onSubmit={onCreateMovement}
      />
    </div>
  );
}